import { useEffect, useState } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { KpiBenchmarks } from 'benchmarking-domain/kpi-benchmark';
import { KpiCategory, KpiDefinition } from 'benchmarking-domain/kpi-definition';
import { KpiStatistics, KpiValues } from 'benchmarking-domain/kpi-value';
import { NumberHelper } from 'helpers/number';

interface HeatmapProps {
  category: KpiCategory;
  scopeKpiValues: KpiValues | undefined;
  benchmarks?: KpiBenchmarks | undefined;
  statistics?: Map<string, KpiStatistics> | undefined;
  plantId: string;
}

interface KpiHeatmapDataModel {
  kpi: KpiDefinition;
  projectValue: number | undefined;
  benchmarkedSite: number | undefined;
  bottomQuartile: number | undefined;
  median: number | undefined;
  topQuartile: number | undefined;
}

const KpiHeatmap: React.FC<HeatmapProps> = ({ category, scopeKpiValues, benchmarks, statistics, plantId }) => {
  const [kpiDefinitions, setKpiDefinitions] = useState<KpiHeatmapDataModel[]>([]);
  const [displayCurrentProjectValue, setDisplayCurrentProjectValue] = useState(false);

  useEffect(() => {
    const kpis = category.kpiDefinitions.map((kpi) => {
      const kpiDefinition = new KpiDefinition(
        kpi.id,
        kpi.name,
        kpi.description,
        kpi.whatIsBetter,
        kpi.unit,
        kpi.calculationPhrase,
        kpi.type,
        kpi.requiresCurrencyConversion,
        kpi.sourceInputValue
      );

      const kpiBenchmark = benchmarks?.kpisBenchmarks[kpi.id];
      const kpiStatistics = statistics?.get(kpi.id);

      const facilityId = kpi.dynamicKpiParentName ? `${plantId}@${kpi.dynamicKpiParentName}` : plantId;
      const scopeValueId =
        kpi.dynamicKpiParentName && kpi.dynamicKpiParentName.length > 0
          ? kpi.id + `@${kpi.dynamicKpiParentName}`
          : kpi.id;

      return {
        kpi: kpiDefinition,
        projectValue: scopeKpiValues?.[scopeValueId],
        benchmarkedSite: kpiStatistics?.facilityValues[facilityId],
        bottomQuartile: kpiBenchmark?.bottomQuartile,
        median: kpiBenchmark?.median,
        topQuartile: kpiBenchmark?.topQuartile,
      };
    });
    setKpiDefinitions(kpis);
    setDisplayCurrentProjectValue(
      kpis.some((k) => k.projectValue && benchmarks?.kpisBenchmarks[k.kpi.id] && k.projectValue !== k.benchmarkedSite)
    );
  }, [category, benchmarks, statistics, plantId, scopeKpiValues]);

  const quartileColors = {
    none: undefined,
    top: 'green',
    third: '#80c342',
    second: '#ffeb99',
    bottom: '#f0ad4e',
  };

  const getColorForBenchmark = (
    value: number | undefined,
    firstQuartile: number | undefined,
    median: number | undefined,
    fourthQuartile: number | undefined
  ) => {
    if (value === undefined) {
      return quartileColors.none;
    }

    if (fourthQuartile && value >= fourthQuartile) {
      return quartileColors.top;
    }
    if (median && value >= median) {
      return quartileColors.third;
    }
    if (firstQuartile) {
      if (value > firstQuartile) {
        return quartileColors.second;
      } else {
        return quartileColors.bottom;
      }
    }

    return quartileColors.none;
  };

  return (
    <Paper sx={{ padding: 2 }}>
      {/* Legend */}
      <Box display="flex" alignItems="center" marginBottom={2}>
        <Typography variant="body1" marginRight={1}>
          Legend:
        </Typography>
        <Stack direction="row" spacing={3}>
          <Typography
            variant="caption"
            sx={{ backgroundColor: quartileColors.top }}
            paddingLeft={2}
            paddingRight={2}
            paddingTop={1}
            paddingBottom={1}
          >
            Top Quartile
          </Typography>
          <Typography
            variant="caption"
            sx={{ backgroundColor: quartileColors.third }}
            paddingLeft={2}
            paddingRight={2}
            paddingTop={1}
            paddingBottom={1}
          >
            3rd Quartile
          </Typography>
          <Typography
            variant="caption"
            sx={{ backgroundColor: quartileColors.second }}
            paddingLeft={2}
            paddingRight={2}
            paddingTop={1}
            paddingBottom={1}
          >
            2nd Quartile
          </Typography>
          <Typography
            variant="caption"
            sx={{ backgroundColor: quartileColors.bottom }}
            paddingLeft={2}
            paddingRight={2}
            paddingTop={1}
            paddingBottom={1}
          >
            Bottom Quartile
          </Typography>
        </Stack>
      </Box>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>KPI</TableCell>
            <TableCell>Units</TableCell>
            <TableCell>Scoring</TableCell>
            {displayCurrentProjectValue && <TableCell align="right">Current project (not the latest)</TableCell>}
            <TableCell align="right">Benchmarked site</TableCell>
            <TableCell align="right">Bottom Quartile</TableCell>
            <TableCell align="right">Median</TableCell>
            <TableCell align="right">Top Quartile</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {kpiDefinitions.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.kpi.name}</TableCell>
              <TableCell>{row.kpi.unit}</TableCell>
              <TableCell>{KpiDefinition.getScoringDescription(row.kpi.whatIsBetter)}</TableCell>
              {displayCurrentProjectValue && (
                <TableCell align="right">
                  <Box
                    sx={{
                      backgroundColor: getColorForBenchmark(
                        row.projectValue,
                        row.bottomQuartile,
                        row.median,
                        row.topQuartile
                      ),
                      padding: '4px 8px',
                      color: '#fff',
                      borderRadius: 1,
                      textAlign: 'center',
                    }}
                  >
                    {row.projectValue}
                  </Box>
                </TableCell>
              )}
              <TableCell align="right">
                <Box
                  sx={{
                    backgroundColor: getColorForBenchmark(
                      row.benchmarkedSite,
                      row.bottomQuartile,
                      row.median,
                      row.topQuartile
                    ),
                    padding: '4px 8px',
                    color: '#fff',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  {row.benchmarkedSite}
                </Box>
              </TableCell>
              <TableCell align="right">{NumberHelper.formatNumber(row.bottomQuartile)}</TableCell>
              <TableCell align="right">{NumberHelper.formatNumber(row.median)}</TableCell>
              <TableCell align="right">{NumberHelper.formatNumber(row.topQuartile)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default KpiHeatmap;
